import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { fetchAvatarsBulk } from '../../utils/avatars';
import { currentLocale } from '../../i18n';
import RankBadge from '../elo/RankBadge';
import SteamAvatar from '../common/SteamAvatar';
import './SeasonOverview.css';

/**
 * Season overview page showing season metadata, MVP/most-active highlights,
 * and the full ELO leaderboard with win/loss records and streaks.
 */
export default function SeasonOverview() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/season`)
      .then(r => r.json())
      .then(async (d) => {
        const steamIds = [];
        if (d.mvp?.steamid64) steamIds.push(d.mvp.steamid64);
        if (d.most_active?.steamid64) steamIds.push(d.most_active.steamid64);
        (d.leaderboard || []).forEach(p => { if (p.steamid64) steamIds.push(p.steamid64); });
        if (steamIds.length > 0) await fetchAvatarsBulk(steamIds);
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="season-page"><div className="loading-screen"><div className="loader" /></div></div>;
  }

  if (!data || data.error) {
    return <div className="season-page"><p>{t('season.noActiveSeason')}</p></div>;
  }

  const { season, leaderboard, mvp, most_active, total_ranked } = data;

  const startDate = season.start_date
    ? new Date(season.start_date).toLocaleDateString(currentLocale(), { year: 'numeric', month: 'long', day: 'numeric' })
    : '?';

  return (
    <div className="season-page">
      <div className="season-header">
        <h1 className="page-title">{season.name}</h1>
        <div className="season-meta">
          <span>{t('season.startedOn', { date: startDate })}</span>
          <span>{t('season.matchesCount', { count: season.total_matches })}</span>
          <span>{t('season.rankedPlayers', { count: total_ranked })}</span>
        </div>
      </div>

      {/* Highlights */}
      <div className="season-highlights">
        {mvp && (
          <div className="season-highlight-card gold" onClick={() => mvp.steamid64 && navigate(`/stats/players/${mvp.steamid64}`)}>
            <span className="season-highlight-label">{t('season.mvp')}</span>
            <div className="season-highlight-player">
              <SteamAvatar steamid64={mvp.steamid64} size={40} />
              <div>
                <span className="season-highlight-name">{mvp.player_name}</span>
                <div className="season-highlight-stats">
                  <RankBadge elo={mvp.elo} rank={mvp.rank} size="small" />
                  <span>{mvp.elo} ELO</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {most_active && most_active.player_name !== mvp?.player_name && (
          <div className="season-highlight-card blue" onClick={() => most_active.steamid64 && navigate(`/stats/players/${most_active.steamid64}`)}>
            <span className="season-highlight-label">{t('season.mostActive')}</span>
            <div className="season-highlight-player">
              <SteamAvatar steamid64={most_active.steamid64} size={40} />
              <div>
                <span className="season-highlight-name">{most_active.player_name}</span>
                <div className="season-highlight-stats">
                  <span>{t('season.matchesCount', { count: most_active.matches_played })}</span>
                  <span>{t('season.winPct', { pct: most_active.win_rate })}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Leaderboard */}
      <div className="season-leaderboard">
        <h2 className="season-section-title">{t('season.ranking')}</h2>
        <div className="season-lb-list">
          {leaderboard.map((p, i) => (
            <button
              key={p.steamid64 || i}
              className="season-lb-row"
              onClick={() => p.steamid64 && navigate(`/stats/players/${p.steamid64}`)}
            >
              <span className={`season-lb-pos ${i < 3 ? `top-${i + 1}` : ''}`}>#{i + 1}</span>
              <SteamAvatar steamid64={p.steamid64} size={32} />
              <span className="season-lb-name">{p.player_name}</span>
              <RankBadge elo={p.elo} rank={p.rank} size="small" />
              <span className="season-lb-elo">{p.elo}</span>
              <span className="season-lb-record">{p.wins || 0}W {p.losses || 0}L</span>
              {p.current_streak > 0 && (
                <span className="season-lb-streak hot">{p.current_streak}</span>
              )}
              {p.current_streak < 0 && (
                <span className="season-lb-streak cold">{Math.abs(p.current_streak)}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
